import { getLastActivityAt, matchesOrigin, reloadFrameOrSelf } from "./embed";

export const CONNECT_AUTH_READY = "connect:auth-ready";
export const CONNECT_AUTH_TOKEN = "connect:auth-token";
export const CONNECT_AUTH_EXPIRED = "connect:auth-expired";
export const CONNECT_AUTH_OK = "connect:auth-ok";
export const CONNECT_AUTH_ERROR = "connect:auth-error";
export const CONNECT_AUTH_LOGOUT = "connect:auth-logout";
export const CONNECT_AUTH_LOGOUT_OK = "connect:auth-logout-ok";

interface ConnectAuthMessage {
    type?: string;
    accessToken?: string;
    token?: string;
    tenantId?: string;
    reason?: string;
    correlationId?: string;
    detail?: string;
}

export interface ConnectHandshakeConfig {
    connectOrigin: string;
    parentOrigin: string;
    returnUrl: string;
    rootUrl: string;
}

export interface ConnectShellBridgeConfig {
    connectOrigin: string | null | undefined;
    parentOrigin: string | null | undefined;
    rootUrl: string;
}

const READY_RETRY_INTERVAL_MS = 2000;
const READY_MAX_ATTEMPTS = 5;
const EXCHANGE_MAX_ATTEMPTS = 3;
// Comfortably inside the shortest possible ticket lifetime, which is floored at
// 90 minutes server-side (CookieExpiration).
const SESSION_KEEPALIVE_INTERVAL_MS = 30 * 60 * 1000;
// Keeps a short window from turning the keepalive into a poll. A higher floor would
// invert the guarantee, refreshing less often than the shortest sessions live.
const KEEPALIVE_MIN_INTERVAL_MS = 2 * 60 * 1000;
// How far ahead of the shutdown window the shell warns (ShellModel). The keepalive has
// to land before that, or a held session still shows the user an expiry warning.
const SHELL_WARNING_LEAD_MINUTES = 5;
// Delay before the first keepalive, which is also when the shutdown window is learned.
// Short enough to precede the earliest possible warning, long enough to stay off the
// page-load path.
const KEEPALIVE_FIRST_RUN_MS = 15 * 1000;
// How long the host gets to react to a terminal session before the iframe puts
// up its own notice. Mirrors the Q88 embed (QEN-8650).
const EXPIRED_OVERLAY_DELAY_MS = 10000;
// How recently someone must have interacted for a silent recovery to be worth
// doing. One keepalive interval: any more recent and the keepalive would have
// held the ticket, so there would be no 401 to recover from.
const RECENT_ACTIVITY_WINDOW_MS = SESSION_KEEPALIVE_INTERVAL_MS;
// Consecutive keepalive failures before saying so. One is a blip the 401 path
// still recovers from; repeated ones mean the ticket is not being held at all.
const KEEPALIVE_FAILURE_REPORT_THRESHOLD = 2;
const EXPIRED_OVERLAY_ID = "connect-session-expired-overlay";

export const resolveConnectParentOrigin = (
    allowlist: string | null | undefined,
    parentOriginHint: string | null | undefined
): string | null => {
    const tokens = (allowlist || "").split(/\s+/).filter((t: string) => t.startsWith("http"));
    if (!tokens.length) return null;

    const candidates: string[] = [];
    if (parentOriginHint) candidates.push(parentOriginHint);
    if (document.referrer) candidates.push(document.referrer);

    for (const candidate of candidates) {
        let origin: string;
        try {
            origin = new URL(candidate).origin;
        } catch (e) {
            continue;
        }
        if (tokens.some((t) => matchesOrigin(origin, t))) return origin;
    }
    return null;
};

const buildUrl = (rootUrl: string, path: string): string => {
    const base = rootUrl && rootUrl.length ? rootUrl : "/";
    return base.charAt(base.length - 1) === "/" ? base + path : base + "/" + path;
};

const appendParentOrigin = (returnUrl: string, parentOrigin: string): string => {
    if (!parentOrigin || returnUrl.includes("parentOrigin=")) return returnUrl;
    const hashIndex = returnUrl.indexOf("#");
    const base = hashIndex >= 0 ? returnUrl.slice(0, hashIndex) : returnUrl;
    const hash = hashIndex >= 0 ? returnUrl.slice(hashIndex) : "";
    const sep = base.includes("?") ? "&" : "?";
    return base + sep + "parentOrigin=" + encodeURIComponent(parentOrigin) + hash;
};

export const exchangeConnectSession = (rootUrl: string, accessToken: string, tenantId: string): Promise<string | undefined> => {
    return fetch(buildUrl(rootUrl, "api/ConnectShell/connect-session"), {
        method: "POST",
        credentials: "include",
        headers: { Authorization: "Bearer " + accessToken, "veson-tenant-id": tenantId }
    }).then((response: Response) => {
        if (!response.ok) throw new Error("connect-session exchange failed: " + response.status);
        if (typeof response.json !== "function") return undefined;
        return response.json().then(
            (body: { CompanyCode?: string } | null) => (body && body.CompanyCode) || undefined,
            () => undefined
        );
    });
};

export const refreshConnectSession = (rootUrl: string): Promise<number> => {
    return fetch(buildUrl(rootUrl, "api/ConnectShell/connect-session/refresh"), {
        method: "POST",
        credentials: "include"
    }).then((response: Response) => {
        if (!response.ok) throw new Error("connect-session refresh failed: " + response.status);
        // Per-tenant, so it is read rather than configured. Zero is never a failed refresh:
        // the ticket was re-issued before this body was parsed.
        return Promise.resolve()
            .then(() => response.json())
            .then(
                (body: { inactiveShutdownMinutes?: number } | null) =>
                    body?.inactiveShutdownMinutes ?? 0,
                () => 0
            );
    });
};

export const clearConnectSession = (rootUrl: string): Promise<void> => {
    return fetch(buildUrl(rootUrl, "api/ConnectShell/connect-logout"), {
        method: "POST",
        credentials: "include",
        keepalive: true
    }).then((response: Response) => {
        if (!response.ok) throw new Error("connect-logout failed: " + response.status);
    });
};

/**
 * Last-resort notice rendered inside the iframe when the session is over. The
 * postMessage to the host is the primary path; this exists because that message
 * is silently lost whenever the host is not listening — origin mismatch, bridge
 * not mounted, mid-navigation — and without it the frame just sits there looking
 * alive with a dead session behind it. Sign-out offers no reload: the session is
 * gone, so reloading would only land on the Veslink login page.
 */
const showSessionOverlay = (label: string, text: string, offerReload: boolean): void => {
    if (document.getElementById(EXPIRED_OVERLAY_ID)) return;

    const overlay = document.createElement("div");
    overlay.id = EXPIRED_OVERLAY_ID;
    overlay.setAttribute("role", "alertdialog");
    overlay.setAttribute("aria-label", label);
    overlay.style.cssText = [
        "position:fixed", "top:0", "right:0", "bottom:0", "left:0",
        "z-index:2147483647", "display:flex", "align-items:center",
        "justify-content:center", "background:rgba(255,255,255,0.94)",
        "font-family:sans-serif", "text-align:center", "padding:24px"
    ].join(";");

    const message = document.createElement("p");
    message.textContent = text;

    const card = document.createElement("div");
    card.appendChild(message);
    if (offerReload) {
        const button = document.createElement("button");
        button.type = "button";
        button.textContent = "Reload";
        button.addEventListener("click", () => reloadFrameOrSelf());
        card.appendChild(button);
    }
    overlay.appendChild(card);
    document.body.appendChild(overlay);
};

// ---- Handshake (anonymous defer-auth shell) -------------------------------------------------

export const runConnectHandshake = (config: ConnectHandshakeConfig): void => {
    const targetOrigin = resolveConnectParentOrigin(config.connectOrigin, config.parentOrigin);
    if (!targetOrigin) return; // not framed by an allowed Connect origin; cannot proceed securely

    let settled = false;
    let stopped = false;
    let attempts = 0;
    let exchangeAttempts = 0;
    let retryTimer: number | undefined;

    const stop = (): void => {
        stopped = true;
        if (retryTimer !== undefined) {
            window.clearTimeout(retryTimer);
            retryTimer = undefined;
        }
    };

    const sendAuthOk = (correlationId: string | undefined, company?: string): void => {
        const message: { type: string; correlationId: string | undefined; company?: string } =
            { type: CONNECT_AUTH_OK, correlationId };
        if (company) message.company = company;
        window.parent.postMessage(message, targetOrigin);
    };

    const sendAuthError = (reason: string, correlationId: string | undefined): void => {
        window.parent.postMessage({ type: CONNECT_AUTH_ERROR, reason, correlationId }, targetOrigin);
    };

    const sendReady = (): void => {
        if (retryTimer !== undefined) {
            window.clearTimeout(retryTimer);
            retryTimer = undefined;
        }
        if (stopped || settled) return;
        if (attempts >= READY_MAX_ATTEMPTS) { stop(); return; }
        attempts += 1;
        window.parent.postMessage({ type: CONNECT_AUTH_READY }, targetOrigin);
        retryTimer = window.setTimeout(sendReady, READY_RETRY_INTERVAL_MS);
    };

    window.addEventListener("message", (event: MessageEvent) => {
        if (event.source !== window.parent || event.origin !== targetOrigin) return;
        const data: ConnectAuthMessage = event.data || {};

        if (data.type === CONNECT_AUTH_TOKEN) {
            const accessToken = data.accessToken || data.token;
            if (settled || !accessToken || !data.tenantId) return;
            const correlationId = data.correlationId;
            settled = true;
            stop();
            exchangeAttempts += 1;
            exchangeConnectSession(config.rootUrl, accessToken, data.tenantId)
                .then((company) => {
                    sendAuthOk(correlationId, company);
                    window.location.replace(appendParentOrigin(config.returnUrl, targetOrigin));
                })
                .catch(() => {
                    if (exchangeAttempts >= EXCHANGE_MAX_ATTEMPTS) {
                        stop();
                        sendAuthError("session-failed", correlationId);
                        return;
                    }
                    settled = false;
                    stopped = false;
                    sendReady();
                });
        } else if (data.type === CONNECT_AUTH_ERROR) {
            if (data.reason === "no-session") stop();
        }
    });

    sendReady();
};

// ---- Authenticated shell bridge -------------------------------------------------------------

export interface ConnectShellBridge {
    handleSessionExpiry: () => void;
    handleCompanySuperseded: (company: string | null | undefined) => void;
}

export const initConnectShellBridge = (config: ConnectShellBridgeConfig): ConnectShellBridge | null => {
    const targetOrigin = resolveConnectParentOrigin(config.connectOrigin, config.parentOrigin);
    if (!targetOrigin || window.parent === window) {
        return null;
    }

    let latestAccessToken: string | null = null;
    let latestTenantId: string | null = null;
    let latestCorrelationId: string | undefined;
    let recovering = false;
    let exchangeFailures = 0;
    let superseded = false;
    let pendingTenantSwitch = false;
    let teardownPromise: Promise<void> | null = null;
    let sessionEnded = false;
    let expired = false;
    let expiredOverlayTimer: number | undefined;
    let keepaliveFailures = 0;

    const scheduleExpiredOverlay = (): void => {
        if (expiredOverlayTimer !== undefined) return;
        expiredOverlayTimer = window.setTimeout(
            () => showSessionOverlay("IMOS session expired", "Your IMOS session has expired.", true),
            EXPIRED_OVERLAY_DELAY_MS
        );
    };

    const reExchangeAndReload = (accessToken: string, tenantId: string, correlationId: string | undefined): void => {
        exchangeConnectSession(config.rootUrl, accessToken, tenantId)
            .then((company) => {
                const message: { type: string; correlationId: string | undefined; company?: string } =
                    { type: CONNECT_AUTH_OK, correlationId };
                if (company) message.company = company;
                window.parent.postMessage(message, targetOrigin);
                window.location.reload();
            })
            .catch(() => {
                recovering = false;
                exchangeFailures += 1;
                if (exchangeFailures >= EXCHANGE_MAX_ATTEMPTS) {
                    expired = true;
                    window.parent.postMessage({ type: CONNECT_AUTH_ERROR, reason: "session-failed", correlationId }, targetOrigin);
                    scheduleExpiredOverlay();
                }
            });
    };

    const handleCompanySuperseded = (company: string | null | undefined): void => {
        if (superseded) return;
        superseded = true;
        window.parent.postMessage({ type: CONNECT_AUTH_ERROR, reason: "company-superseded", company }, targetOrigin);
    };

    const handleSessionExpiry = (): void => {
        if (superseded || recovering || pendingTenantSwitch || expired) return;

        // The 401 that lands here is usually a background poll, and re-authenticating
        // on it with nobody present is the loop this guard exists to stop. So the
        // question is whether someone is at the keyboard now, not whether they ever
        // were: "interacted at some point since load" would still recover once for a
        // tab used briefly and then abandoned hours ago.
        //
        // Reaching a 401 at all means the keepalive did not hold the ticket, so the
        // two cases separate cleanly at one keepalive interval: a present user whose
        // refresh failed interacted seconds ago, an absent one did not. That also
        // leaves reExchangeAndReload its full EXCHANGE_MAX_ATTEMPTS, since 401s
        // landing milliseconds apart are all equally recent.
        if (Date.now() - getLastActivityAt() > RECENT_ACTIVITY_WINDOW_MS) {
            expired = true;
            window.parent.postMessage(
                { type: CONNECT_AUTH_ERROR, reason: "session-failed", correlationId: latestCorrelationId },
                targetOrigin
            );
            scheduleExpiredOverlay();
            return;
        }

        recovering = true;
        window.parent.postMessage({ type: CONNECT_AUTH_EXPIRED }, targetOrigin);
        if (latestAccessToken && latestTenantId) {
            reExchangeAndReload(latestAccessToken, latestTenantId, latestCorrelationId);
        }
    };

    const handleTokenMessage = (data: ConnectAuthMessage): void => {
        const accessToken = data.accessToken || data.token;
        if (!accessToken || !data.tenantId) return;
        if (superseded) return;
        latestAccessToken = accessToken;
        latestTenantId = data.tenantId;
        latestCorrelationId = data.correlationId;
        if (pendingTenantSwitch) {
            pendingTenantSwitch = false;
            recovering = true;
            const tenantId = data.tenantId;
            const correlationId = data.correlationId;
            const proceed = (): void => reExchangeAndReload(accessToken, tenantId, correlationId);
            const afterTeardown = teardownPromise ?? Promise.resolve();
            afterTeardown.then(proceed, proceed);
            return;
        }

        if (recovering) reExchangeAndReload(accessToken, data.tenantId, data.correlationId);
    };

    const handleLogoutMessage = (data: ConnectAuthMessage): void => {
        const correlationId = data.correlationId;
        const isSignOut = data.reason !== "tenant-switch";
        latestAccessToken = null;
        latestTenantId = null;
        latestCorrelationId = undefined;
        recovering = false;
        exchangeFailures = 0;
        sessionEnded = isSignOut;
        if (!isSignOut) {
            pendingTenantSwitch = true;
            superseded = false;
        }
        // Sign-out leaves the frame in place rather than reloading it: Connect is
        // about to navigate the whole page away, and a reload shows the Veslink
        // login page and, on a dirty form, a beforeunload prompt that can strand
        // that navigation. The notice only appears if the host stalls.
        const finish = (): void => {
            if (isSignOut) {
                window.setTimeout(
                    () => showSessionOverlay("IMOS signed out", "You have been signed out of IMOS.", false),
                    EXPIRED_OVERLAY_DELAY_MS
                );
            }
        };
        const teardown = clearConnectSession(config.rootUrl)
            .then(() => {
                window.parent.postMessage({ type: CONNECT_AUTH_LOGOUT_OK, correlationId }, targetOrigin);
                finish();
            })
            .catch(() => {
                window.parent.postMessage({ type: CONNECT_AUTH_ERROR, reason: "logout-failed", correlationId }, targetOrigin);
                finish();
            });
        if (!isSignOut) teardownPromise = teardown;
    };

    window.addEventListener("message", (event: MessageEvent) => {
        if (event.source !== window.parent || event.origin !== targetOrigin) return;
        const data: ConnectAuthMessage = event.data || {};
        if (data.type === CONNECT_AUTH_TOKEN) handleTokenMessage(data);
        else if (data.type === CONNECT_AUTH_LOGOUT) handleLogoutMessage(data);
    });

    // The forms ticket is stamped with a fixed expiry when it is issued and nothing
    // slides it, so without this an actively-used session still hits its boundary,
    // 401s, and gets recovered by a full page reload that discards open work.
    // Unconditional: the host's idle watchdog is the only bound, and gating here would
    // reap the app under a user whose interaction the host can see but this frame cannot.
    // Re-armed from the reported window, which is per-tenant, at half of it so one lost
    // request is survivable.
    let keepaliveInterval = SESSION_KEEPALIVE_INTERVAL_MS;
    // The first run is soon rather than an interval away: the window is only known from a
    // response, so waiting would start every session on the 30-minute default and warn any
    // tenant reaped sooner than that before the keepalive had run once. Still a timer, not
    // a direct call, so a fetch that throws synchronously cannot escape initialisation.
    let firstKeepaliveRun = true;
    const armKeepalive = (): void => {
        // Read at arm time, not cached: the interval is adopted from the response that
        // immediately precedes this call, and a cached value would always be one late.
        const delay = firstKeepaliveRun ? KEEPALIVE_FIRST_RUN_MS : keepaliveInterval;
        firstKeepaliveRun = false;
        window.setTimeout(runKeepalive, delay);
    };
    const adoptReportedWindow = (inactiveShutdownMinutes: number): void => {
        if (inactiveShutdownMinutes <= 0) return;   // keeps the default
        // Must beat the shell's warning, not just the reap: that warning is a fixed five
        // minutes ahead of the window, so half the window is too late once the window is
        // under ten. A minute of margin under the warning covers the request itself.
        const beatsWarning = inactiveShutdownMinutes - SHELL_WARNING_LEAD_MINUTES - 1;
        const halfWindow = inactiveShutdownMinutes / 2;
        keepaliveInterval = Math.min(
            Math.max(Math.min(halfWindow, beatsWarning) * 60000, KEEPALIVE_MIN_INTERVAL_MS),
            SESSION_KEEPALIVE_INTERVAL_MS
        );
    };

    const runKeepalive = (): void => {
        if (sessionEnded || superseded || expired) return;
        if (pendingTenantSwitch) {
            armKeepalive();
            return;
        }
        refreshConnectSession(config.rootUrl).then(
            (inactiveShutdownMinutes: number) => {
                keepaliveFailures = 0;
                adoptReportedWindow(inactiveShutdownMinutes);
            },
            (error: unknown) => {
                keepaliveFailures += 1;
                // Not fatal on its own: the 401 path still recovers the session and
                // the next interval retries. But a keepalive that never succeeds
                // shows up as sessions quietly expiring under active users, hours
                // later and several layers from the cause, so say it here where the
                // cause is still visible.
                if (keepaliveFailures >= KEEPALIVE_FAILURE_REPORT_THRESHOLD) {
                    console.warn(
                        "Connect session keepalive is failing; the Veslink ticket is no longer being extended.",
                        error
                    );
                }
            }
        ).then(armKeepalive, armKeepalive);
    };

    armKeepalive();

    return { handleSessionExpiry, handleCompanySuperseded };
};

export const CONNECT_COMPANY_PIN_PARAM = "connectCompany";
const CONNECT_COMPANY_MISMATCH_MARKER = "connectCompanyMismatch";

let activeConnectCompanyPin: string | null = null;
let activeCompanySupersededNotifier: ((company: string | null | undefined) => void) | null = null;

export const registerConnectExportContext = (
    companyPin: string | null | undefined,
    onCompanySuperseded: (company: string | null | undefined) => void
): void => {
    activeConnectCompanyPin = companyPin || null;
    activeCompanySupersededNotifier = onCompanySuperseded;
};

export const getConnectExportCompanyPin = (): string | null => activeConnectCompanyPin;

export const handleConnectExportResponse = (bodyText: string | null | undefined): boolean => {
    if (!bodyText || !activeCompanySupersededNotifier) return false;
    if (bodyText.indexOf(CONNECT_COMPANY_MISMATCH_MARKER) < 0) return false;
    let company: string | null = null;
    try {
        const parsed = JSON.parse(bodyText);
        if (!parsed || !parsed[CONNECT_COMPANY_MISMATCH_MARKER]) return false;
        company = parsed.company || null;
    } catch (e) {
        return false;
    }
    activeCompanySupersededNotifier(company);
    return true;
};
